<template>
  <v-dialog :value="sectionEditor.show" :width="1000" @input="handleClosePopup" :retain-focus="false">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>

      <v-card-text style="max-height: 70vh; overflow-y: auto">
        <div>
          <label class="label-input">Section Name</label>
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field v-model="fields.title" label="Name" hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-checkbox small v-model="fields.enableAnchor" label="Enable Anchor" hide-details />
            </v-col>
          </v-row>
        </div>

        <div class="d-flex align-center">
          <v-checkbox v-model="fields['containerFullWidth']" label="Layout Full Width" />
        </div>

        <div class="mb-6">
          <label class="label-input">Header</label>
          <ckeditor :editor="editor" v-model="fields.titleContent" :config="editorConfig" />
        </div>

        <div class="mb-6">
          <label class="label-input mb-4 d-flex align-center gap-2" htmlFor="imageShow" style="width: fit-content">
            <span>Image</span>
            <input class="ml-2 cursor-pointer" type="checkbox" v-model="fields.image.show" id="imageShow" />
          </label>
          <div class="group-item">
            <v-select v-model="fields.image.position" :items="positionOptions" label="Image Position" />
            <UploadImage
              @callback="obj => handleUploadImage(obj)"
              :image="{ imageSrc: fields.image.url }"
              :height="`200`"
              :typeUpload="'image'"
              @callbackDelete="() => handleRemoveUploadImage()"
            />
          </div>
        </div>

        <div class="mb-6">
          <div class="d-flex justify-space-between align-center mb-4">
            <label class="label-input mb-0">Items ({{ fields.listItem.length }})</label>
            <v-btn small color="primary" @click="handleAddItem">
              <v-icon size="16" left>mdi-plus</v-icon>
              Add Item
            </v-btn>
          </div>

          <div class="group-item">
            <div v-if="fields.listItem.length === 0" class="text-center pa-4 grey--text group-item">
              No items added yet. Click "Add Item" to get started.
            </div>

            <div v-else>
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    type="number"
                    :rules="[
                      v => Number(v) > 0 || 'Items Per Row must be greater than 0',
                      v => Number(v) <= maxItemsPerRow || `Items Per Row must be <= ${maxItemsPerRow}`,
                    ]"
                    min="1"
                    v-model="fields.itemsPerRow.mobile"
                    label="Items Per Row (Mobile)"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    type="number"
                    :rules="[
                      v => Number(v) > 0 || 'Items Per Row must be greater than 0',
                      v => Number(v) <= maxItemsPerRow || `Items Per Row must be <= ${maxItemsPerRow}`,
                    ]"
                    min="1"
                    v-model="fields.itemsPerRow.desktop"
                    label="Items Per Row (Desktop)"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-checkbox v-model="fields.showNumber" label="Show Number" />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="3">
                  <v-text-field
                    type="number"
                    v-model.number="fields.itemsGridListWidth"
                    label="Items Grid List Width"
                    min="0"
                    suffix="%"
                    :rules="[v => (v >= 0 && v <= 100) || 'Items Grid List Width must be between 0 and 100']"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    type="number"
                    v-model.number="fields.cardBorderRadius"
                    label="Card Border Radius"
                    min="0"
                    suffix="px"
                    :rules="[v => v >= 0 || 'Border Radius must be greater than or equal 0']"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    type="number"
                    v-model.number="fields.itemImageWidthDesktop"
                    label="Item Image Width (Desktop)"
                    min="0"
                    suffix="px"
                    :rules="[v => v >= 0 || 'Item Image Width must be greater than or equal 0']"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    type="number"
                    v-model.number="fields.itemImageWidthMobile"
                    label="Item Image Width (Mobile)"
                    min="0"
                    suffix="px"
                    :rules="[v => v >= 0 || 'Item Image Width must be greater than or equal 0']"
                  />
                </v-col>
              </v-row>
              <v-expansion-panels class="mt-4" accordion>
                <v-expansion-panel v-for="(item, i) in fields.listItem" :key="item._key">
                  <v-expansion-panel-header>
                    <div class="d-flex justify-space-between align-center" style="width: 100%">
                      <div class="d-flex align-center">
                        <div class="item-thumbnail mr-3">
                          <img v-if="item.image" :src="item.image" alt="" />
                          <v-icon v-else color="grey lighten-1">mdi-image</v-icon>
                        </div>
                        <span
                          class="thumbnail-text line-clamp-1"
                          v-html="item.htmlText.replace(/<[^>]*>/g, '').trim()"
                        ></span>
                      </div>
                      <v-btn icon small @click.stop="handleRemoveItem(i)">
                        <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div class="pa-2">
                      <div class="mb-4">
                        <UploadImage
                          @callback="obj => handleUploadImageItem(obj, i)"
                          :image="{ imageSrc: item.image }"
                          :height="`200`"
                          :typeUpload="'image'"
                          @callbackDelete="() => handleRemoveUploadItem(i)"
                        />
                      </div>
                      <div class="mb-4">
                        <v-text-field v-model="item.number" label="Number" hide-details />
                      </div>
                      <div class="item-text">
                        <label class="mb-2 d-block">Text</label>
                        <ckeditor :editor="editor" v-model="item.htmlText" :config="editorConfig" />
                      </div>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </div>
        </div>

        <div>
          <label class="label-input">Section Settings</label>
          <div class="group-item">
            <div>
              <div class="font-weight-medium">Spacing</div>
              <v-row>
                <v-col cols="6">
                  <v-checkbox v-model="fields.topPadding" label="Top Padding" dense hide-details />
                </v-col>
                <v-col cols="6">
                  <v-checkbox v-model="fields.bottomPadding" label="Bottom Padding" dense hide-details />
                </v-col>
              </v-row>
            </div>
            <div class="mt-6">
              <div class="font-weight-medium mb-3 mb-2">Container Colors</div>
              <v-row>
                <v-col md="6" cols="12">
                  <ColorPicker
                    v-model="fields.backgroundColor"
                    name="backgroundColor"
                    label="Background Color"
                    :default-color="isTypeItemsGrid04 ? '#f2f2f2' : '#ffffff'"
                  />
                </v-col>
                <v-col md="6" cols="12">
                  <ColorPicker v-model="fields.textColor" name="textColor" label="Text Color" default-color="#000000" />
                </v-col>
              </v-row>
            </div>
            <div class="mt-6">
              <div class="font-weight-medium mb-3 mb-2">Card Colors</div>
              <v-row>
                <v-col md="6" cols="12">
                  <ColorPicker
                    v-model="fields.cardBackgroundColor"
                    name="cardBackgroundColor"
                    label="Background Color"
                    :default-color="isTypeItemsGrid04 ? '#f2f2f2' : '#ffffff'"
                  />
                </v-col>
                <v-col md="6" cols="12">
                  <ColorPicker
                    v-model="fields.cardTextColor"
                    name="cardTextColor"
                    label="Text Color"
                    default-color="#000000"
                  />
                </v-col>
              </v-row>
            </div>
          </div>
        </div>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { updateHtmlFromData } from '../common/html/update';
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import ColorPicker from './common/color-picker.vue';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import { replaceImageUrl } from '../common/utils';
import { sectionType } from '@/views/website/products/components/description/common/types';

export default {
  name: 'items-grid-04-section-editor',
  components: {
    UploadImage,
    ColorPicker,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      positionOptions: [
        {
          text: 'Left',
          value: 'left',
        },
        {
          text: 'Right',
          value: 'right',
        },
      ],
      fields: {
        title: 'Items Grid Media Section',
        enableAnchor: false,
        backgroundColor: '#f2f2f2',
        textColor: '#000000',
        cardBackgroundColor: '#f2f2f2',
        cardTextColor: '#000000',
        titleContent: '',
        listItem: [],
        topPadding: true,
        bottomPadding: true,
        containerFullWidth: false,
        itemsPerRow: {
          mobile: 1,
          desktop: 1,
        },
        cardBorderRadius: 0,
        itemsGridListWidth: 85,
        itemImageWidthDesktop: 110,
        itemImageWidthMobile: 110,
        showNumber: false,
        image: {
          show: false,
          position: 'left',
          url: '',
        },
      },
    };
  },
  computed: {
    maxItemsPerRow() {
      return Math.max(1, this.fields.listItem?.length || 0);
    },
    itemsGridTypeNormalized() {
      return (this.sectionEditor.section.type || '').trim();
    },
    isTypeItemsGrid04() {
      return this.itemsGridTypeNormalized === sectionType.sectionItemsGrid04;
    },
    isTypeItemsGrid05() {
      return this.itemsGridTypeNormalized === sectionType.sectionItemsGrid05;
    },
  },
  mounted() {
    if (this.sectionEditor.sectionExtract) {
      this.fields = {
        ...this.fields,
        ...this.sectionEditor.sectionExtract,
      };
      this.ensureItemKeys();
      this.normalizeItemsPerRow();
    }
  },
  watch: {
    sectionEditor: {
      handler(newVal) {
        if (newVal.sectionExtract) {
          this.fields = {
            ...this.fields,
            ...newVal.sectionExtract,
          };
          this.ensureItemKeys();
          this.normalizeItemsPerRow();
        }
      },
      deep: true,
    },
  },
  methods: {
    generateItemKey() {
      return `ig02_${Date.now().toString(36)}_${Math.random()
        .toString(36)
        .slice(2, 10)}`;
    },
    ensureItemKeys() {
      if (!Array.isArray(this.fields.listItem)) this.$set(this.fields, 'listItem', []);

      this.fields.listItem = this.fields.listItem.map(item => {
        if (item && item._key) return item;
        return { ...(item || {}), _key: this.generateItemKey() };
      });
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      this.ensureItemKeys();
      this.normalizeItemsPerRow();
      const updateHtml = updateHtmlFromData.sectionItemsGrid04(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtml,
      });
      this.$emit('close');
    },
    handleUploadImageItem(obj, i) {
      if (obj && obj.imageSrc) {
        this.fields.listItem[i].image = obj.imageSrc;
      }
    },
    handleRemoveUploadItem(i) {
      this.fields.listItem[i].image = '';
    },
    handleUploadImage(obj) {
      if (obj && obj.imageSrc) {
        this.fields.image.url = obj.imageSrc;
      }
    },
    handleRemoveUploadImage() {
      this.fields.image.url = '';
    },
    handleAddItem() {
      this.fields.listItem.push({
        _key: this.generateItemKey(),
        image: replaceImageUrl(
          'https://cdn.skyfastimg.com/images/2026/04/01/image-69ccbfaaec071dd3b7cd743a.png?w=500&h=50&q=80',
        ),
        htmlText:
          '<div data-attribute="text-item" class="items-grid-item-text"> <h2><strong>#1 Relieves Cough & Phlegm*</strong></h2> <p> Soothes wheezing and coughing and helps loosen phlegm to make coughs more productive. </p> </div>',
      });
      this.normalizeItemsPerRow();
    },
    handleRemoveItem(i) {
      this.fields.listItem.splice(i, 1);
      this.normalizeItemsPerRow();
    },
    normalizeItemsPerRow() {
      const max = this.maxItemsPerRow;
      const mobile = Number(this.fields.itemsPerRow?.mobile);
      const desktop = Number(this.fields.itemsPerRow?.desktop);

      if (!this.fields.itemsPerRow) {
        this.$set(this.fields, 'itemsPerRow', { mobile: max, desktop: max });
        return;
      }

      const safeMobile = Number.isNaN(mobile) || mobile < 1 ? 1 : mobile;
      const safeDesktop = Number.isNaN(desktop) || desktop < 1 ? 1 : desktop;

      this.fields.itemsPerRow.mobile = Math.min(safeMobile, max);
      this.fields.itemsPerRow.desktop = Math.min(safeDesktop, max);
    },
  },
};
</script>

<style scoped lang="scss">
.ck-editor__editable {
  min-height: 100px;
}
.item-thumbnail {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}
.thumbnail-text {
  flex: 1;
  p {
    margin: 0 !important;
  }
}
</style>
